import { useCallback, useEffect, useRef, useState } from "react"

import type { CrewPulseEvent } from "@/types/collab"

/** Pixels from the top within which we treat the user as "at the top" — new
 *  arrivals prepend silently with the beat rather than surfacing the pill. */
const TOP_THRESHOLD_PX = 8

function isAtTop(): boolean {
  if (typeof window === "undefined") return true
  return window.scrollY <= TOP_THRESHOLD_PX
}

export interface PulseStreamState {
  /** The events currently rendered (may lag `events` while the pill holds). */
  displayed: CrewPulseEvent[]
  /** Ids that just arrived — the row plays the one-shot arrival beat. */
  newIds: Set<string>
  /** Count of new events held behind the "N new — tap to see" pill (0 hides). */
  pendingCount: number
  /** Reveal the held events: prepend, mark them new, scroll to top. */
  reveal: () => void
}

/**
 * OKS-473 — Crew Pulse full-stream live-arrival controller (UX Spec §1.2).
 *
 * Given the server-ordered `events` prop (newest-first, recomputed on every
 * realtime refetch / poll), it decides how new arrivals surface:
 *   - **at scroll-top** → prepend immediately, play the one-shot beat on the
 *     new rows (no scroll jump needed);
 *   - **scrolled down** → HOLD the current list and raise `pendingCount` so the
 *     page can show a "N new — tap to see" pill instead of yanking the scroll.
 *     `reveal()` then prepends, marks the new rows, and scrolls to top.
 *
 * The prop→state reconciliation runs DURING render (React's "adjust state from
 * props" pattern — no effect, so no set-state-in-effect feedback loop); the
 * `reveal` callback reads the latest events/displayed via effect-mirrored refs.
 */
export function usePulseStream(events: CrewPulseEvent[]): PulseStreamState {
  const [displayed, setDisplayed] = useState<CrewPulseEvent[]>(events)
  const [newIds, setNewIds] = useState<Set<string>>(new Set())
  const [pendingCount, setPendingCount] = useState(0)
  const [prevEvents, setPrevEvents] = useState<CrewPulseEvent[]>(events)

  // Reconcile the new `events` prop against what's currently shown, in render.
  if (events !== prevEvents) {
    setPrevEvents(events)
    const shownIds = new Set(displayed.map((e) => e.id))
    const incoming = events.filter((e) => !shownIds.has(e.id))

    if (incoming.length === 0) {
      // No arrivals — sync removals / reorders, but only when nothing is held
      // behind the pill (don't clobber a pending reveal).
      if (pendingCount === 0) setDisplayed(events)
    } else if (isAtTop()) {
      setDisplayed(events)
      setNewIds(new Set(incoming.map((e) => e.id)))
      setPendingCount(0)
    } else {
      setPendingCount(incoming.length)
    }
  }

  // Effect-mirrored refs so `reveal` (a stable callback) can read the latest
  // values without ref writes during render.
  const displayedRef = useRef(displayed)
  const latestRef = useRef(events)
  useEffect(() => {
    displayedRef.current = displayed
  }, [displayed])
  useEffect(() => {
    latestRef.current = events
  }, [events])

  const reveal = useCallback(() => {
    const latest = latestRef.current
    const shownIds = new Set(displayedRef.current.map((e) => e.id))
    const revealed = latest.filter((e) => !shownIds.has(e.id))

    setDisplayed(latest)
    setNewIds(new Set(revealed.map((e) => e.id)))
    setPendingCount(0)
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [])

  return { displayed, newIds, pendingCount, reveal }
}
